import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from "@etudescarre/constants";
import { Button, Modal } from "@etudescarre/ui";
import { WarningIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

const WARNING_SECONDS = Math.ceil(IDLE_WARNING_MS / 1000);
// Stored before sign-out so the login screen can explain why the user landed
// there, surviving any redirect that happens in between.
export const LOGOUT_REASON_KEY = "ec:logout-reason";

/**
 * Signs the authenticated user out after a period of inactivity. A warning with
 * a countdown is shown shortly before, letting the user keep their session.
 *
 * Mount inside the authenticated layout so it only runs for signed-in users.
 */
export function IdleTimeout() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_SECONDS);

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastReset = useRef(0);
  // While the warning is up we ignore background activity: the user must make an
  // explicit choice (stay or sign out).
  const warningActive = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdown.current) clearInterval(countdown.current);
  }, []);

  const logout = useCallback(async () => {
    clearTimers();
    warningActive.current = false;
    setShowWarning(false);
    sessionStorage.setItem(LOGOUT_REASON_KEY, "inactivite");
    // Sign out first so the session is revoked before we land on the (public)
    // login screen, which would otherwise bounce an authenticated user away.
    await authClient.signOut();
    navigate("/auth/login", { replace: true });
  }, [clearTimers, navigate]);

  const startWarning = useCallback(() => {
    warningActive.current = true;
    setSecondsLeft(WARNING_SECONDS);
    setShowWarning(true);
    countdown.current = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
  }, []);

  // The logout fires when the warning countdown reaches zero.
  useEffect(() => {
    if (showWarning && secondsLeft === 0) {
      logout();
    }
  }, [showWarning, secondsLeft, logout]);

  const scheduleIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(
      startWarning,
      IDLE_TIMEOUT_MS - IDLE_WARNING_MS,
    );
  }, [startWarning]);

  const stayConnected = useCallback(() => {
    clearTimers();
    warningActive.current = false;
    setShowWarning(false);
    scheduleIdle();
  }, [clearTimers, scheduleIdle]);

  useEffect(() => {
    const onActivity = () => {
      if (warningActive.current) return;
      const now = Date.now();
      if (now - lastReset.current < 1000) return; // throttle rescheduling
      lastReset.current = now;
      scheduleIdle();
    };

    scheduleIdle();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      clearTimers();
    };
  }, [scheduleIdle, clearTimers]);

  return (
    <Modal.Root open={showWarning} lazyMount unmountOnExit>
      <Modal.Portal>
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="sm">
            <Modal.Header>
              <div className="flex items-center gap-3">
                <WarningIcon className="h-5 w-5 text-amber-500" />
                <Modal.Title>Toujours là ?</Modal.Title>
              </div>
            </Modal.Header>

            <Modal.Body>
              <p className="text-gray-600 text-sm">
                Par mesure de sécurité, vous allez être déconnecté dans{" "}
                <strong>{secondsLeft}s</strong> en raison de votre inactivité.
              </p>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={logout}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 text-sm hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#1a3b5c]"
                >
                  Se déconnecter
                </button>
                <Button
                  className="flex-1 bg-[#1a3b5c] hover:bg-[#2a4b6c]"
                  onClick={stayConnected}
                  type="button"
                >
                  Rester connecté
                </Button>
              </div>
            </Modal.Body>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Portal>
    </Modal.Root>
  );
}
